import { BorderedBox } from '@/shared/ui/BorderedBox';
import { SectionWrapper } from '@/entities/widget';
import LinkIcon from '@/shared/assets/images/link.svg';
import { OptimizedImage } from '@/shared/ui/OptimizedImage';
import GeminiIcon from '@/shared/assets/images/gemini.svg';
import OpenAIIcon from '@/shared/assets/images/openAI.svg';
import { SourceEnumSchema, type SourceType } from '@inference.engine/widgets/web';
import type { SourcesSectionProps, StoredSource } from './SourcesSection.types';
import { useTranslation } from 'react-i18next';

const SOURCE_ICONS = {
  'Open AI Deep Research': {
    src: OpenAIIcon,
    altKey: 'audienceFlowWidget.sourcesSection.openAiDeepResearchLogo',
  },
  'Gemini, 2025': { src: GeminiIcon, altKey: 'audienceFlowWidget.sourcesSection.geminiLogo2025' },
} as const satisfies Record<SourceType, { src: string; altKey: string }>;

const isKnownSource = (s: StoredSource): s is StoredSource & { source: SourceType } =>
  SourceEnumSchema.safeParse(s.source).success;

export const SourcesSection = ({ sources }: SourcesSectionProps) => {
  const { t } = useTranslation('audiences');

  const knownSources = sources?.filter(isKnownSource);

  if (!knownSources?.length) {
    return null;
  }

  return (
    <SectionWrapper title={t('audienceFlowWidget.sourcesSection.sources')} icon={LinkIcon}>
      <div>
        {knownSources.map(({ source, evidence }, index) => (
          <BorderedBox key={index} className="mb-4">
            <div className="text-body-md text-muted-foreground">
              <div className="flex items-center gap-2">
                <OptimizedImage
                  src={SOURCE_ICONS[source].src}
                  alt={t(SOURCE_ICONS[source].altKey)}
                  width={20}
                  height={20}
                />
                <p className="text-body-md text-foreground">{source}</p>
              </div>
              <ol className="ml-12 list-decimal">
                {evidence.map((item, itemIndex) => (
                  <li key={itemIndex}>{item}</li>
                ))}
              </ol>
            </div>
          </BorderedBox>
        ))}
      </div>
    </SectionWrapper>
  );
};
